<template>
  <div class="nh notification-history">
    <b-card class="planner-list-card nh-shell">
      <div class="gradient-line-wrapper" />

      <!-- Header -->
      <div class="nh-head table-header">
          <div>
          <h5 class="mb-0 resource-title">{{ $t("Notifications") }}</h5>
          <small class="text-muted">
            <template v-if="unreadCount > 0">
              <span class="nh-head__unread">{{ unreadCount }} {{ $t("unread") }}</span>
              <span class="nh-head__sep">·</span>
            </template>
            {{ filteredNotifications.length }} {{ $t("total") }}
          </small>
        </div>
        <div class="nh-head__actions">
          <b-button
            v-if="unreadCount > 0"
            variant="outline-primary"
            class="action-btn-filter text-nowrap"
            @click="markAllAsRead"
          >
            <feather-icon icon="CheckIcon" class="mr-50" />
            <span>{{ $t("Mark all as read") }}</span>
          </b-button>
          <b-button
            variant="outline-primary"
            class="btn-icon action-btn-filter"
            :disabled="loading"
            v-b-tooltip.hover.top
            :title="$t('Refresh')"
            @click="fetchNotifications"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              :class="{ 'nh-spin': loading }"
            />
          </b-button>
        </div>
      </div>

      <!-- Pagination — same as planner list pages (once only) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="page-size-wrapper">
          <label class="page-size-label">{{ $t("Show") }}:</label>
          <v-select
            v-model="perPage"
            :options="pageSizeOptions"
            :reduce="(option) => option.value"
            label="label"
            :clearable="false"
            :searchable="false"
            append-to-body
            class="page-size-select-compact"
            @input="onPageSizeChange"
          />
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }}
            <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }}
            <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ displayNotifications.length }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="displayNotifications.length"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
          />
        </div>
      </div>

      <!-- Filters -->
      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group nh-filter-group d-flex align-items-center flex-grow-1"
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input filter-field-item nh-filter-search"
              :placeholder="$t('Search notifications…')"
              @input="currentPage = 1"
            />

            <div class="nh-seg filter-field-item">
              <button
                type="button"
                class="nh-seg__btn"
                :class="{ 'is-on': filterMode === 'all' }"
                @click="setFilter('all')"
              >
                {{ $t("All") }}
              </button>
              <button
                type="button"
                class="nh-seg__btn"
                :class="{ 'is-on': filterMode === 'unread' }"
                @click="setFilter('unread')"
              >
                {{ $t("Unread") }}
                <em v-if="unreadCount">{{ unreadCount }}</em>
              </button>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Skeleton -->
      <div v-if="loading && !notifications.length" class="nh-table">
        <div v-for="n in 7" :key="'sk-' + n" class="nh-row nh-row--skel">
          <div class="nh-skel" style="width: 64px; height: 12px" />
          <div class="nh-skel-lines">
            <div class="nh-skel" style="width: 36%; height: 11px" />
            <div class="nh-skel" style="width: 70%; height: 10px" />
          </div>
          <div class="nh-skel" style="width: 56px; height: 10px" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!paginatedNotifications.length" class="nh-empty">
        <feather-icon icon="InboxIcon" size="28" class="mb-50" />
        <div class="font-weight-bold mb-25">{{ $t("No notifications") }}</div>
        <div class="text-muted font-small-3">
          {{
            filterMode === "unread"
              ? $t("No unread notifications.")
              : searchQuery
              ? $t("No results match your search.")
              : $t("Nothing to show right now.")
          }}
        </div>
      </div>

      <!-- Table list -->
      <div v-else class="nh-table">
        <div class="nh-table__head">
          <span>{{ $t("Type") }}</span>
          <span>{{ $t("Notification") }}</span>
          <span>{{ $t("When") }}</span>
        </div>

        <div
          v-for="group in visibleGroups"
          :key="'g-' + group.label"
          class="nh-group"
        >
          <div class="nh-day">{{ group.label }}</div>

          <div
              v-for="(item, itemIdx) in group.items"
              :key="notificationRowKey(item, itemIdx)"
            class="nh-row"
            :class="{
              'is-unread': !item.isRead,
              'is-clickable': canNavigateNotification(item),
              'is-loading': routingId === notificationRowKey(item, itemIdx),
            }"
            role="button"
            tabindex="0"
              @click="handleCardClick(item, itemIdx)"
            @keydown.enter.prevent="handleCardClick(item, itemIdx)"
          >
            <div class="nh-row__type">
              <span
                class="nh-row__type-dot"
                :class="'tone-' + getMeta(item).tone"
              />
              {{ getMeta(item).label }}
            </div>

            <div class="nh-row__msg">
              <div class="nh-row__title-line">
                <span v-if="!item.isRead" class="nh-row__unread" />
                <span class="nh-row__title">{{ item.title }}</span>
                    <b-spinner
                      v-if="routingId === notificationRowKey(item, itemIdx)"
                      small
                  class="ml-50"
                />
                </div>
              <p class="nh-row__body">{{ item.body }}</p>
                </div>

            <div class="nh-row__when">
              <span>{{ formatReadableTime(item.date) }}</span>
              <button
                    v-if="!item.isRead"
                type="button"
                class="nh-row__mark"
                v-b-tooltip.hover.top
                :title="$t('Mark as read')"
                    @click.stop="markAsRead(item)"
              >
                <feather-icon icon="CheckIcon" size="13" />
              </button>
              <feather-icon
                v-else-if="canNavigateNotification(item)"
                icon="ChevronRightIcon"
                size="14"
                class="nh-row__go"
              />
                </div>
              </div>
        </div>
      </div>

    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BFormInput,
  BRow,
  BCol,
  BPagination,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { mapActions } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { resolveNotificationNavigation } from "@/utils/notificationNavigation";

export default {
  name: "NotificationHistory",
  components: {
    BCard,
    BButton,
    BSpinner,
    BFormInput,
    BRow,
    BCol,
    BPagination,
    vSelect,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      loading: false,
      notifications: [],
      routingId: null,
      filterMode: "all",
      searchQuery: "",
      currentPage: 1,
      perPage: 25,
      pageSizeOptions: [
        { label: "15", value: 15 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
    };
  },
  watch: {
    $route() {
      this.routingId = null;
    },
    totalPages(pages) {
      if (this.currentPage > pages) this.currentPage = pages;
    },
  },
  computed: {
    unreadCount() {
      return this.filteredNotifications.filter((n) => !n.isRead).length;
    },
    filteredNotifications() {
      const rights =
        this.$store.state.rights[this.$route.name] ||
        this.$store.state.rights["Notifications"] ||
        this.$store.state.rights["notification-history"];
      let list = this.notifications || [];
      if (
        rights &&
        rights.notificationTypes &&
        rights.notificationTypes.length > 0
      ) {
        list = list.filter((n) =>
          this.notificationAllowedForRights(n, rights.notificationTypes)
        );
      }
      return list;
    },
    displayNotifications() {
      let list = this.filteredNotifications;
      if (this.filterMode === "unread") {
        list = list.filter((n) => !n.isRead);
      }
      const q = String(this.searchQuery || "")
        .trim()
        .toLowerCase();
      if (q) {
        list = list.filter((n) => {
          const hay = `${n.title || ""} ${n.body || ""}`.toLowerCase();
          return hay.includes(q);
        });
      }
      return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.displayNotifications.length / this.perPage) || 1
      );
    },
    paginatedNotifications() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.displayNotifications.slice(start, start + this.perPage);
    },
    displayedRange() {
      const total = this.displayNotifications.length;
      if (!total) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, total);
      return { start, end };
    },
    visibleGroups() {
      if (!this.paginatedNotifications.length) return [];

      const groups = { Today: [], Yesterday: [], Earlier: [] };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      this.paginatedNotifications.forEach((item) => {
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        if (itemDate.getTime() === today.getTime()) groups.Today.push(item);
        else if (itemDate.getTime() === yesterday.getTime())
          groups.Yesterday.push(item);
        else groups.Earlier.push(item);
      });

      return Object.entries(groups)
        .filter(([, items]) => items.length > 0)
        .map(([label, items]) => ({
          label: this.$t(label),
          items,
        }));
    },
  },
  created() {
    this.fetchNotifications();
  },
  methods: {
    ...mapActions(["get", "getString"]),

    setFilter(mode) {
      this.filterMode = mode;
      this.currentPage = 1;
    },

    onPageSizeChange() {
      this.currentPage = 1;
    },

    async fetchNotifications() {
      this.loading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Notifications/LoadData?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const response = await this.get(obj);
        if (response && response.data) {
          this.notifications = response.data;
        } else {
          this.notifications = response || [];
        }
      } catch (error) {
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },

    notificationRowKey(notification, index) {
      const type = notification.type || "n";
      const localID =
        notification.localID != null ? notification.localID : "";
      const id = notification.id != null ? notification.id : index;
      return `${type}-${localID}-${id}-${index}`;
    },

    isSyntheticNotification(notification) {
      if (!notification) return false;
      const id = Number(notification.id);
      if (!id) return true;
      return String(notification.type || "").toLowerCase() === "inventory";
    },

    notificationAllowedForRights(n, types) {
      const title = (n.title || "").toLowerCase();
      const type = String(n.type || "").toLowerCase();
      const isInventory = type === "inventory";
      const isChat = type === "chat" || title.includes("message");
      const isStaff =
        type === "staff" ||
        title.includes("staff") ||
        title.includes("application") ||
        title.includes("applicant");
      const isStudent =
        type === "students" ||
        type === "student" ||
        title.includes("student") ||
        title.includes("marks") ||
        title.includes("fee") ||
        title.includes("attendance");

      if (isInventory && !types.includes("inventory")) return false;
      if (isChat && !types.includes("chat")) return false;
      if (isStaff && !types.includes("staff")) return false;
      if (isStudent && !types.includes("students")) return false;
      return true;
    },

    async markAsRead(notification) {
      if (notification.isRead) return;
      if (this.isSyntheticNotification(notification)) {
        notification.isRead = true;
        return;
      }
      try {
        await this.getString({
          url: `${this.$store.state.domain}Notifications/MarkAsRead?db=${this.$store.state.userData.db}&id=${notification.id}`,
          token: this.$store.state.userData.token,
        });
        notification.isRead = true;
      } catch (error) {
        // silent
      }
    },

    async markAllAsRead() {
      try {
        await this.getString({
          url: `${this.$store.state.domain}Notifications/MarkAllAsRead?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        });
        this.notifications.forEach((n) => {
          if (!this.isSyntheticNotification(n)) n.isRead = true;
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("Done"),
            icon: "CheckCircleIcon",
            text: this.$t("All notifications marked as read"),
            variant: "success",
          },
        });
      } catch (error) {
        // silent
      }
    },

    formatReadableTime(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      const now = new Date();
      const diffInHrs = (now - date) / (1000 * 60 * 60);

      if (diffInHrs < 1) {
        const mins = Math.max(1, Math.floor((now - date) / (1000 * 60)));
        return `${mins}m`;
      }
      if (diffInHrs < 24) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
    },

    canNavigateNotification(item) {
      return !!resolveNotificationNavigation(
        item,
        this.$store.state.rights || {}
      ).ok;
    },

    handleCardClick(item, itemIdx) {
      if (this.routingId) return;
      this.routingId = this.notificationRowKey(item, itemIdx);

      if (!item.isRead) this.markAsRead(item);

      const target = resolveNotificationNavigation(
        item,
        this.$store.state.rights || {}
      );

      if (!target.ok) {
        this.routingId = null;
        if (target.reason === "no-rights") {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("No Access"),
              icon: "LockIcon",
              text: this.$t("You do not have rights to open this record."),
              variant: "warning",
            },
          });
        }
        return;
      }

      const menuParent = (this.$store.state.menu || []).find((el) =>
        (el.children || []).some(
          (ch) =>
            ch.route === target.name ||
            (target.aliases || []).includes(ch.route)
        )
      );
      if (menuParent) this.$store.commit("setCurrentMenu", menuParent);

      this.$router
        .push({
          name: target.name,
          params: target.params || {},
          query: target.query || {},
        })
        .catch(() => {
          this.$router
            .push({ name: target.name, params: target.params || {} })
            .catch(() => {
            this.routingId = null;
          });
        });
    },

    getMeta(item) {
      const t = `${item.title || ""} ${item.type || ""}`.toLowerCase();
      if (t.includes("inventory"))
        return { label: this.$t("Inventory"), tone: "warning" };
      if (t.includes("application") || t.includes("applicant"))
        return { label: this.$t("Application"), tone: "primary" };
      if (t.includes("transfer"))
        return { label: this.$t("Transfer"), tone: "info" };
      if (t.includes("leave"))
        return { label: this.$t("Leave"), tone: "info" };
      if (t.includes("probation"))
        return { label: this.$t("Probation"), tone: "warning" };
      if (t.includes("marks") || t.includes("exam"))
        return { label: this.$t("Exam"), tone: "success" };
      if (t.includes("fee") || t.includes("payment"))
        return { label: this.$t("Fee"), tone: "success" };
      if (t.includes("staff"))
        return { label: this.$t("Staff"), tone: "primary" };
      if (t.includes("student"))
        return { label: this.$t("Student"), tone: "info" };
      if (t.includes("chat") || t.includes("message"))
        return { label: this.$t("Message"), tone: "secondary" };
      return { label: this.$t("Alert"), tone: "secondary" };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

.nh {
  /* Theme tokens from plannerListTable.scss */
  --nh-line: #e0d8ff;
  --nh-muted: #64748b;
  --nh-text: #2d1b69;
  --nh-soft: #faf9ff;
  --nh-accent: #7367f0;
  --nh-accent-2: #7c5dfa;
  --nh-hover: #f3f0ff;
  --nh-border-soft: #ebe4ff;
}

.notification-history {
  .planner-list-card.nh-shell {
    overflow: visible;
  }
}

/* Header */
.nh-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;

  &__unread {
    color: var(--nh-accent);
    font-weight: 700;
  }

  &__sep {
    margin: 0 0.25rem;
    color: #b8a9ff;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
}

.nh-filter-group {
  gap: 0.5rem;
  width: 100%;
}

.nh-filter-search {
  flex: 1 1 auto !important;
  min-width: 180px !important;
  max-width: none !important;
  width: auto !important;
}

.nh-seg {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto !important;
  min-width: 0 !important;
  height: 46px;
  padding: 0.2rem;
  background: #fff;
  border: 1.5px solid var(--nh-line);
  border-radius: 12px;

  &__btn {
    border: 0;
    background: transparent;
    color: var(--nh-muted);
    font-size: 0.8rem;
    font-weight: 600;
    padding: 0.4rem 0.9rem;
    border-radius: 9px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    height: 100%;
    white-space: nowrap;

    em {
      font-style: normal;
      font-size: 0.68rem;
      font-weight: 700;
      background: #eeedfd;
      color: var(--nh-accent);
      border-radius: 6px;
      padding: 0.05rem 0.35rem;
      min-width: 1.1rem;
      text-align: center;
    }

    &.is-on {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      color: #fff;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.25);

      em {
        background: rgba(255, 255, 255, 0.25);
        color: #fff;
      }
    }
  }
}

/* Table */
.nh-table {
  width: 100%;
  border: 1px solid var(--nh-line);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 0.25rem;
}

.nh-table__head {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) 100px;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--nh-line);
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7c6fd6;
}

.nh-day {
  padding: 0.4rem 1rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #7c6fd6;
  background: #f8f7ff;
  border-bottom: 1px solid var(--nh-border-soft);
}

.nh-row {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) 100px;
  gap: 0.75rem;
  align-items: start;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f1f0f8;
  background: #fff;
  outline: none;
  transition: background 0.1s ease;

  &:last-child {
    border-bottom: 0;
  }

  &.is-clickable {
  cursor: pointer;
  }

  &.is-clickable:hover,
  &:focus-visible {
    background: var(--nh-hover);
  }

  &.is-unread {
    background: #fcfbff;
  }

  &.is-unread .nh-row__title {
    font-weight: 700;
    color: #2d1b69;
  }

  &.is-loading {
    opacity: 0.6;
    pointer-events: none;
  }

  &__type {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #5e5873;
    padding-top: 0.15rem;
    white-space: nowrap;
  }

  &__type-dot {
    width: 7px;
    height: 7px;
  border-radius: 50%;
  flex-shrink: 0;

    &.tone-primary {
      background: #7367f0;
    }
    &.tone-info {
      background: #00cfe8;
    }
    &.tone-success {
      background: #28c76f;
    }
    &.tone-warning {
      background: #ff9f43;
    }
    &.tone-secondary {
      background: #82868b;
    }
  }

  &__msg {
    min-width: 0;
  }

  &__title-line {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0.1rem;
  }

  &__unread {
    width: 6px;
    height: 6px;
  border-radius: 50%;
    background: var(--nh-accent);
    flex-shrink: 0;
  }

  &__title {
    font-size: 0.86rem;
    font-weight: 600;
    color: #2d1b69;
    line-height: 1.3;
  }

  &__body {
    margin: 0;
    font-size: 0.78rem;
    color: var(--nh-muted);
    line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

  &__when {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
    padding-top: 0.15rem;
  }

  &__mark {
    border: 0;
    background: transparent;
    color: #94a3b8;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: grid;
    place-items: center;
    cursor: pointer;
    padding: 0;

    &:hover {
      background: #eeedfd;
      color: var(--nh-accent);
    }
  }

  &__go {
    color: #b8a9ff;
  }

  &.is-clickable:hover &__go {
    color: var(--nh-accent);
  }
}

.nh-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--nh-muted);
  border: 1px dashed var(--nh-line);
  border-radius: 12px;
  background: var(--nh-soft);
}

.nh-row--skel {
  pointer-events: none;
}

.nh-skel-lines {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-top: 0.15rem;
}

.nh-skel {
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f0f8, #faf9ff, #f1f0f8);
  background-size: 200% 100%;
  animation: nhPulse 1.15s ease-in-out infinite;
}

@keyframes nhPulse {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.nh-spin {
  animation: nhSpin 0.85s linear infinite;
}

@keyframes nhSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767.98px) {
  .nh-table__head {
    display: none;
  }

  .nh-row {
    grid-template-columns: 1fr auto;
    gap: 0.35rem 0.75rem;

    &__type {
      grid-column: 1 / -1;
      padding-top: 0;
    }

    &__when {
      align-self: start;
    }
  }

  .nh-filter-group {
    flex-wrap: wrap;
  }

  .nh-filter-search,
  .nh-seg {
    width: 100%;
  }
}
</style>
